import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { useGalleryConciergeProducts } from './useGalleryConciergeProducts';
import type { GalleryItemData } from './components/GalleryItem/GalleryItem';
import { useCallback } from 'react';
import { seededUnit } from '@apps/card/utils/seededRandom';
import { isConciergeServiceCategory } from '@apps/card/concierge/conciergeConfig';
import { useGalleryPlacementSeed } from './useGalleryPlacementSeed';

// Index of first Concierge card
const FIRST_CONCIERGE_INDEX = 10;
// Base interval between cards
const CONCIERGE_INTERVAL = 7;
// + variation from CONCIERGE_INTERVAL
const CONCIERGE_VARIATION = 2;
const useConciergeTiles = () => {
  // The tiles' spacing comes from the placement seed (PRNT-5494), so it is the same on the server
  // and the client and holds still for the placement period. Their order comes from
  // useGalleryConciergeProducts.
  const { currentCategory } = useCardCategoryContext();
  const seed = useGalleryPlacementSeed();
  const { conciergeProducts } = useGalleryConciergeProducts();

  const injectTiles = useCallback(
    (tiles: GalleryItemData[]) => {
      if (!isConciergeServiceCategory(currentCategory)) {
        return tiles;
      }
      const tilesWithConcierge = [...tiles].map(t => t);

      const products = conciergeProducts[currentCategory];
      let insertionIndex = FIRST_CONCIERGE_INDEX;
      products.forEach((product, productsIndex) => {
        const variance = Math.floor(seededUnit(`${seed}:concierge`, productsIndex) * (CONCIERGE_VARIATION + 1));
        if (productsIndex !== 0) {
          insertionIndex += CONCIERGE_INTERVAL + variance;
        }
        tilesWithConcierge.splice(insertionIndex, 0, { ...product, index: productsIndex, type: 'concierge' });
      });

      return tilesWithConcierge;
    },
    [currentCategory, seed, conciergeProducts],
  );

  return injectTiles;
};

export default useConciergeTiles;
